import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { Building2, Eye, Heart, MapPin, Phone, Store, Tag, Users } from 'lucide-react';
import {
  TOGGLE_FAVORITE_BRAND,
  TOGGLE_FAVORITE_SHOP,
  TOGGLE_FAVORITE_STORE,
} from '@/graphql/mutations';
import { useAuth } from '@/context/AuthContext';
import { ENTITY_TYPENAMES, useFollowToggle } from '@/hooks/useFollowToggle';
import { trackBrandWebsiteClick, trackFavorite } from '@/lib/analytics';
import { Button, Img, Skeleton, StatPill, isPublicCount } from '@/components/ui';
import { Breadcrumbs } from '@/components/ui/Breadcrumbs';
import type { Crumb } from '@/seo/structuredData';
import { displayName } from '@/seo/text';
import type { DetailStatus } from '@/utils/detailStatus';
import { DetailLoadError, DetailNotFound } from './DetailStatusViews';
import { EntityFollowContext } from './entityFollow';
import { SocialLinks } from './SocialLinks';
import { CONTENT_COLUMN_SIZES, ImagePresets } from '@/utils/safeImageUrl';
import type {
  Brand,
  Media,
  PromotionMinimal,
  Shop,
  Socials,
  Store as StoreEntity,
} from '@/types';

export type EntityKind = 'brand' | 'shop' | 'store';

/** Fields shared by Brand / Shop / Store that the layout relies on. */
export interface CommonEntity {
  id: string;
  name: string;
  logo?: Media;
  coverImage?: Media;
  website?: string;
  phoneNumber?: string;
  mobileNumber?: string;
  socials?: Socials;
  viewCounts?: number;
  subscriberCounts?: number;
  isFavorite?: boolean;
  isSubscribed?: boolean;
  promotions?: PromotionMinimal[];
}

interface EntityDetailLayoutProps {
  kind: EntityKind;
  entity: Brand | Shop | StoreEntity | null;
  /** `DetailState.status` (web plan W0.2): skeleton, entity, not found or retry. */
  status: DetailStatus;
  /** `DetailState.errorCode`, for the retry state's copy. */
  errorCode?: string | null;
  onRetry: () => void;
  /** Main-column sections (promotions grid, stores, categories, tags…). */
  children?: ReactNode;
  /** Extra aside content (e.g. a LocationMapCard) rendered below stats/contact/socials. */
  asideExtra?: ReactNode;
  addressLine?: string | null;
  /**
   * The page's one H1, from its head (`head.h1`, src/seo/templates — web plan
   * W0.6). Falls back to the entity's name.
   */
  h1?: string | null;
  /** A line under the H1, e.g. a store's own name when the H1 formula leaves it out. */
  subtitle?: string | null;
  /**
   * The page's trail, `head.breadcrumbs` unchanged: the same array as its
   * BreadcrumbList JSON-LD (web plan W0.8). Shown above the cover.
   */
  breadcrumbs?: readonly Crumb[] | null;
  /**
   * The Promotions pill's number when the page lists more than the entity's
   * own promotions (a store page also lists its brand's offers, web plan
   * W0.10). Defaults to `entity.promotions.length`.
   */
  promotionCount?: number;
  /** The page's own head (`<PageHead/>`, web plan W5.1), rendered in every state. */
  pageHead?: ReactNode;
}

const FAVORITE_MUTATIONS = {
  brand: TOGGLE_FAVORITE_BRAND,
  shop: TOGGLE_FAVORITE_SHOP,
  store: TOGGLE_FAVORITE_STORE,
} as const;

export function EntityDetailLayout({
  kind,
  entity,
  status,
  errorCode,
  onRetry,
  children,
  asideExtra,
  addressLine,
  h1,
  subtitle,
  breadcrumbs,
  promotionCount: promotionCountProp,
  pageHead,
}: EntityDetailLayoutProps) {
  const navigate = useNavigate();
  const { token } = useAuth();

  const common: CommonEntity | null = entity;

  const [favorite, setFavorite] = useState(false);
  // The header's Follow button, its follower count and the sections' follow
  // CTAs (EntityFollowContext) share this one toggle.
  const follow = useFollowToggle(kind, common);
  const { subscribed, subscribing, followers } = follow;

  const [toggleFavorite, { client }] = useMutation(FAVORITE_MUTATIONS[kind]);

  const identifyEntity = (e: CommonEntity) =>
    client.cache.identify({
      __typename: (e as { __typename?: string }).__typename ?? ENTITY_TYPENAMES[kind],
      id: e.id,
    });

  useEffect(() => {
    if (!common) return;
    setFavorite(!!common.isFavorite);
  }, [common]);

  const handleFavorite = () => {
    if (!common) return;
    if (!token) {
      navigate('/login');
      return;
    }
    const next = !favorite;
    const cacheId = identifyEntity(common);
    setFavorite(next);
    toggleFavorite({
      variables: { id: common.id },
      update: (cache) => {
        cache.modify({ id: cacheId, fields: { isFavorite: () => next } });
      },
    })
      .then(() => {
        if (next) trackFavorite({ contentType: kind, itemId: common.id });
      })
      .catch(() => {
        setFavorite(!next);
        client.cache.modify({ id: cacheId, fields: { isFavorite: () => !next } });
      });
  };

  if (status === 'loading') {
    return (
      <div className="mx-auto w-full max-w-7xl px-4 pb-16">
        {pageHead}
        {/* The breadcrumb row (one text-sm line) and the cover below, in the
            same boxes as the loaded page: a different height here shifted
            the whole page when the data landed. */}
        <Skeleton className="mt-4 h-5 w-64 max-w-full rounded-md" />
        <Skeleton className="mt-4 h-56 w-full rounded-3xl md:h-72" />
        <div className="relative z-10 -mt-12 flex flex-wrap items-end gap-4 px-6">
          <Skeleton className="h-24 w-24 rounded-2xl" />
          <div className="flex-1 space-y-2 pb-2">
            <Skeleton className="h-7 w-56 max-w-full rounded-lg" />
            <Skeleton className="h-4 w-40 max-w-full rounded-lg" />
          </div>
        </div>
        <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_340px]">
          <div className="space-y-4">
            <Skeleton className="h-6 w-44 rounded-lg" />
            <Skeleton className="h-48 w-full rounded-2xl" />
            <Skeleton className="h-48 w-full rounded-2xl" />
          </div>
          <div className="space-y-4">
            <Skeleton className="h-16 w-full rounded-2xl" />
            <Skeleton className="h-28 w-full rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  if (status === 'not_found') {
    return (
      <div className="mx-auto w-full max-w-7xl px-4 py-16">
        {pageHead}
        <DetailNotFound kind={kind} />
      </div>
    );
  }

  if (status === 'error' || !common) {
    return (
      <div className="mx-auto w-full max-w-7xl px-4 py-16">
        {pageHead}
        <DetailLoadError
          kind={kind}
          errorCode={errorCode}
          onRetry={onRetry}
          onBack={() => navigate(-1)}
        />
      </div>
    );
  }

  const FallbackIcon = kind === 'brand' ? Building2 : Store;
  const promotionCount = promotionCountProp ?? common.promotions?.length ?? 0;
  const hasContact = Boolean(common.phoneNumber || common.mobileNumber);

  return (
    <div className="mx-auto w-full max-w-7xl px-4 pb-16">
      {pageHead}
      <Breadcrumbs crumbs={breadcrumbs} className="mt-4" />

      {/* Cover banner */}
      <div className="relative mt-4 h-56 overflow-hidden rounded-3xl md:h-72">
        <Img
          media={common.coverImage}
          transform={ImagePresets.hero}
          sizes={CONTENT_COLUMN_SIZES}
          priority
          alt={`${displayName(common.name)} cover`}
          className="h-full w-full object-cover"
          fallback={
            <div className="h-full w-full bg-gradient-to-br from-primary/60 to-secondary/60" />
          }
        />
      </div>

      {/* Header row overlapping the cover */}
      <div className="relative z-10 -mt-12 flex flex-wrap items-end gap-4 px-6">
        <div className="flex h-24 w-24 shrink-0 items-center justify-center self-start overflow-hidden rounded-2xl border-4 border-background bg-surface shadow-lg">
          <Img
            media={common.logo}
            transform={ImagePresets.logo}
            sizes="96px"
            priority
            alt={displayName(common.name)}
            className="h-full w-full object-contain"
            fallback={<FallbackIcon size={36} className="text-ink-tertiary" />}
          />
        </div>

        {/* Phones: the title takes a full row under the logo and the buttons
            (order-last), so the formula H1 ("{Brand} Sale & Offers") is never
            squeezed into a one-word column beside them. From sm up it sits
            between them, starting just below the cover (the row begins 3rem
            up inside it) and growing downwards, so a two-line H1 or a store's
            subtitle and address never ride up over the cover image. */}
        <div className="order-last min-w-0 basis-full pb-1 sm:order-none sm:flex-1 sm:basis-0 sm:self-start sm:pt-14">
          <h1 className="text-2xl font-extrabold tracking-tight text-ink md:text-3xl">
            {h1?.trim() || common.name}
          </h1>
          {subtitle?.trim() && (
            <p className="mt-1 text-sm font-semibold text-ink-secondary">{subtitle.trim()}</p>
          )}
          {/* Shown whole (no line clamp): a store's line ends with its city
              (web plan W0.10), which a one-line clamp cut off on phones. */}
          {addressLine && (
            <p className="mt-1 flex items-start gap-1 text-sm text-ink-secondary">
              <MapPin size={14} className="mt-[3px] shrink-0 text-primary" aria-hidden="true" />
              <span className="min-w-0 break-words">{addressLine}</span>
            </p>
          )}
        </div>

        <div className="ml-auto flex items-center gap-3 pb-1">
          <button
            type="button"
            onClick={handleFavorite}
            aria-label={favorite ? 'Remove from favorites' : 'Add to favorites'}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-line bg-surface shadow-sm transition-all duration-200 hover:border-error/40"
          >
            <Heart
              size={20}
              fill={favorite ? 'currentColor' : 'none'}
              className={favorite ? 'text-error' : 'text-ink-secondary'}
            />
          </button>
          <Button
            variant={subscribed ? 'outline' : 'primary'}
            loading={subscribing}
            onClick={follow.toggleFollow}
          >
            {subscribed ? 'Following' : 'Follow'}
          </Button>
        </div>
      </div>

      {/* Body: main column + sticky aside */}
      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_340px]">
        {/* Sections can offer the header's follow action (useEntityFollow). */}
        <EntityFollowContext.Provider
          value={{ subscribed, subscribing, toggleFollow: follow.toggleFollow }}
        >
          <div className="min-w-0">{children}</div>
        </EntityFollowContext.Provider>

        <aside className="space-y-4 self-start lg:sticky lg:top-24">
          {/* Followers and views only from PUBLIC_COUNTER_MIN up (web plan
              W0.6); the promotion count is content, not popularity. */}
          <div className="flex flex-wrap gap-3">
            <StatPill icon={Tag} value={promotionCount} label="Promotions" />
            {isPublicCount(followers) && (
              <StatPill icon={Users} value={followers} label="Followers" />
            )}
            {isPublicCount(common.viewCounts) && (
              <StatPill icon={Eye} value={common.viewCounts} label="Views" />
            )}
          </div>

          {hasContact && (
            <div className="space-y-3 rounded-2xl border border-line bg-card p-4">
              <h3 className="text-sm font-bold text-ink">Contact</h3>
              {common.phoneNumber && (
                <a
                  href={`tel:${common.phoneNumber}`}
                  className="flex items-center gap-2 text-sm text-ink-secondary transition-colors duration-200 hover:text-primary"
                >
                  <Phone size={16} className="shrink-0 text-primary" />
                  {common.phoneNumber}
                </a>
              )}
              {common.mobileNumber && (
                <a
                  href={`tel:${common.mobileNumber}`}
                  className="flex items-center gap-2 text-sm text-ink-secondary transition-colors duration-200 hover:text-primary"
                >
                  <Phone size={16} className="shrink-0 text-primary" />
                  {common.mobileNumber}
                </a>
              )}
            </div>
          )}

          <SocialLinks
            socials={common.socials}
            website={common.website}
            onWebsiteClick={(url) =>
              trackBrandWebsiteClick({ contentType: kind, itemId: common.id, url })
            }
          />

          {asideExtra}
        </aside>
      </div>
    </div>
  );
}

export default EntityDetailLayout;
